/* eslint-disable no-unused-vars */
import {
  startAuthentication as startAuthenticationWeb,
  startRegistration as startRegistrationWeb,
  platformAuthenticatorIsAvailable,
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser'
import { Capacitor } from '@capacitor/core'
import {
  startWebAuthnAuthentication,
  authsvsStartWebAuthnAuthentication,
  startWebAuthnRegistration,
  authsvsStartWebAuthnRegistration,
  type StartWebAuthnAuthenticationSuccess,
  type StartWebAuthnAuthenticationError,
  authenticateWebAuthn,
  authsvsAuthenticateWebAuthn,
  registerWebAuthn,
  authsvsRegisterWebAuthn,
  type AuthenticateWebAuthnError,
  type AuthenticateWebAuthnSuccess,
  type RegisterWebAuthnOutput,
  AuthenticateWebAuthnErrorType,
} from '@egw/api'
import { notifications, NOTIFICATIONS } from '@egw/notifications'
import { Logger } from '@egw/logger'
import { sendEvent } from '@egw/events'
import { isUserUsingMobileDevice } from '../../utils'
import { Passkey } from '../../plugins/passkey'
import { UserGestureDetector, WebAuthnOperation } from '../validators/user-gesture-detector'

const log = new Logger('Passkey Service')
const gestureDetector = UserGestureDetector.getInstance()

// Create a single PasskeyErrorTypes enum to handle all the errors including JSON.parse errors and startAuthentication errors
export enum PasskeyErrorTypes {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  PENDING_USER_UNABLE_TO_AUTHENTICATE = 'PENDING_USER_UNABLE_TO_AUTHENTICATE',
  UNABLE_TO_AUTHENTICATE = 'UNABLE_TO_AUTHENTICATE',
  NO_ACTIVE_WEBAUTHN_REGISTRATION = 'NO_ACTIVE_WEBAUTHN_REGISTRATION',
  INVALID_PUBLIC_KEY_CREDENTIAL = 'INVALID_PUBLIC_KEY_CREDENTIAL',
  S_BLOCKED = 'S_BLOCKED',
  OTP_VERIFICATION_REQUIRED = 'OTP_VERIFICATION_REQUIRED',
  UNABLE_TO_SEND_LOGIN_VERIFICATION_OTP = 'UNABLE_TO_SEND_LOGIN_VERIFICATION_OTP',
  // UNKNOWN error type supposed to handle JSON.parse errors and startAuthentication errors
  UNKNOWN = 'UNKNOWN',
  USER_GESTURE_REQUIRED = 'USER_GESTURE_REQUIRED',
}

export enum AuthsVsPasskeyErrorTypes {
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  NO_PASSKEYS_FOUND = 'NO_PASSKEYS_FOUND',
  INTELLIGENCE_VALIDATION_FAILED = 'INTELLIGENCE_VALIDATION_FAILED',
  DEVICE_INCOMPATIBLE = 'DEVICE_INCOMPATIBLE',
  PLATFORM_SETUP_REQUIRED = 'PLATFORM_SETUP_REQUIRED',
}

export class PasskeyError extends Error {
  errorType?: PasskeyErrorTypes | AuthsVsPasskeyErrorTypes
  constructor(errorType?: PasskeyErrorTypes | AuthsVsPasskeyErrorTypes) {
    super('Passkey service - error starting webauthn authentication')
    this.errorType = errorType
  }
}

const startAuthenticationNative = async (options: string, challenge: string, rpId?: string): Promise<string> =>
  new Promise((resolve, reject) => {
    void Passkey.startAuthentication({
      options,
      challenge,
      rpId,
    })
    Passkey.addListener('CAP_PLUGIN_IOS_PASSKEY_SUCCESS', (data: unknown) => {
      const res = data as {
        clientDataJSON: string
        rawAuthenticatorData: string
        signature: string
        userHandle: string
        rawId: string
        id: string
      }
      const response = {
        id: res.id,
        rawId: res.rawId,
        response: {
          authenticatorData: res.rawAuthenticatorData,
          clientDataJSON: res.clientDataJSON,
          signature: res.signature,
          userHandle: res.userHandle,
        },
        type: 'public-key',
        clientExtensionResults: {},
        authenticatorAttachment: 'platform',
      }
      resolve(JSON.stringify(response))
    })
    Passkey.addListener('CAP_PLUGIN_ANDROID_PASSKEY_SUCCESS', (data: unknown) => {
      const res = data as { data: string }
      // const response = JSON.parse(res.data)
      // const updatedResponse = produce(response, (draft: any) => {
      //   const clientDataJSON = JSON.parse(atob(draft.response.clientDataJSON))
      //   clientDataJSON.origin = 'https://wealth-eg-dev.com'
      //   draft.response.clientDataJSON = btoa(JSON.stringify(clientDataJSON))
      // })
      // resolve(JSON.stringify(updatedResponse))
      resolve(res.data)
    })
    Passkey.addListener('CAP_PLUGIN_NATIVE_PASSKEY_ERROR_OR_CANCELLED', (data: unknown) => {
      reject(new Error(data as string))
    })
  })

const startAuthentication = async (options: string): Promise<string> => {
  if (Capacitor.getPlatform() === 'web') {
    // Validate user gesture before WebAuthn operation
    const gestureValidation = gestureDetector.validateGestureRequirements(WebAuthnOperation.AUTHENTICATION)
    if (!gestureValidation.isValid) {
      log.warn('User gesture validation failed for authentication', {
        reason: gestureValidation.reason,
        browserType: gestureDetector.getBrowserType(),
      })
      const error = new PasskeyError(PasskeyErrorTypes.USER_GESTURE_REQUIRED)
      error.message = gestureDetector.getUserGuidanceMessage()
      throw error
    }
    const res = await startAuthenticationWeb({
      optionsJSON: JSON.parse(options) as PublicKeyCredentialRequestOptionsJSON,
    })
    return JSON.stringify(res)
  } else if (Capacitor.isNativePlatform()) {
    const optionsJSON = JSON.parse(options) as PublicKeyCredentialRequestOptionsJSON
    return await startAuthenticationNative(options, optionsJSON.challenge, optionsJSON.rpId)
  }
  throw new Error('Unsupported platform')
}

export const signInWithPasskey = async (
  email?: string,
  otpChallengeCb?: () => void,
): Promise<AuthenticateWebAuthnSuccess> => {
  try {
    if (typeof PublicKeyCredential === 'undefined' || !('signalUnknownCredential' in PublicKeyCredential)) {
      log.warn('WebAuthn Signal API is not supported in this browser')
    }

    sendEvent('device_requestAuthCredentialOptions_submit', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'auth-options',
      emailProvided: Boolean(email),
    })
    const res = await authsvsStartWebAuthnAuthentication({
      email,
    })
    if (res.__typename === 'StartWebAuthnAuthenticationError') {
      const response = res as StartWebAuthnAuthenticationError
      sendEvent('eg_requestAuthCredentialOptions_fail', {
        eventSource: 'egWealth',
        category: 'signIn',
        subCategory: 'auth-options',
        emailProvided: Boolean(email),
      })
      throw new PasskeyError(response.errorType as unknown as AuthsVsPasskeyErrorTypes)
    } else if (res instanceof Error) {
      sendEvent('eg_requestAuthCredentialOptions_error', {
        eventSource: 'egWealth',
        category: 'signIn',
        subCategory: 'auth-options',
        emailProvided: Boolean(email),
      })
    }

    sendEvent('eg_requestAuthCredentialOptions_success', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'auth-options',
      emailProvided: Boolean(email),
    })

    const keyRes = res as StartWebAuthnAuthenticationSuccess

    sendEvent('device_AuthCredential_start', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'auth-credential',
      emailProvided: Boolean(email),
    })
    const asseRes = await startAuthentication(
      // keyRes.publicKeyCredentialRequestOptions.replace('wealth-eg-dev.com', 'local.wealth-eg-dev.com'),
      keyRes.publicKeyCredentialRequestOptions,
    )

    if ((asseRes as unknown) instanceof Error) {
      sendEvent('device_AuthCredential_error', {
        eventSource: 'egWealth',
        category: 'signIn',
        subCategory: 'auth-credential',
        emailProvided: Boolean(email),
      })
    } else {
      sendEvent('device_AuthCredential_success', {
        eventSource: 'egWealth',
        category: 'signIn',
        subCategory: 'auth-credential',
        emailProvided: Boolean(email),
      })
    }

    sendEvent('device_AuthCredential_submit', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'auth-credential',
      emailProvided: Boolean(email),
    })

    const verificationRes = await authsvsAuthenticateWebAuthn({ publicKeyCredentials: asseRes })
    if (verificationRes.__typename === 'AuthenticateWebAuthnError') {
      const response = verificationRes as AuthenticateWebAuthnError
      // Sign IN Fail
      sendEvent('eg_signIn_fail', {
        eventSource: 'egWealth',
        category: 'signIn',
        subCategory: 'result',
        failureReason: response.errorType,
      })

      //Signal API remove passkey if no passkeys are found
      if (response.errorType === AuthenticateWebAuthnErrorType.NO_PASSKEYS_FOUND) {
        try {
          const credentialResponse = JSON.parse(asseRes) as { id?: string }

          if (credentialResponse.id) {
            await passkeyService.signalUnknownCredential(credentialResponse.id)
            sendEvent('eg_passkeyRemove_signal', {
              eventSource: 'egWealth',
              category: 'signIn',
              subCategory: 'result',
              failureReason: response.errorType,
            })
          }
        } catch (err) {
          log.error('Failed to parse credential response for signal API', err)
        }
      }

      if (response.errorType === AuthenticateWebAuthnErrorType.OTP_VERIFICATION_REQUIRED && otpChallengeCb) {
        otpChallengeCb()
      }
      throw new PasskeyError(response.errorType as unknown as PasskeyErrorTypes)
    }
    // Sign In Success
    sendEvent('eg_signIn_success', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'result',
      userId: (verificationRes as AuthenticateWebAuthnSuccess).customerId,
      authMethod: 'two-factor passkey',
    })

    return verificationRes as AuthenticateWebAuthnSuccess
  } catch (err) {
    sendEvent('eg_signIn_error', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'result',
      error: err,
    })
    // Check if errorType is undefined and set it to UNKNOWN - handles JSON.parse errors and startAuthentication errors
    const error = err as PasskeyError
    if (!error.errorType) {
      error.errorType = PasskeyErrorTypes.UNKNOWN
    }
    throw error
  }
}

export const getIsMobileWithPlatformAuthenticator = async (): Promise<boolean> => {
  const isMobile = isUserUsingMobileDevice().isDeviceMobile
  const hasPlatformAuthenticator: boolean = await platformAuthenticatorIsAvailable()
  return isMobile && hasPlatformAuthenticator
}

const startRegistrationNative = async (options: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const optionsJSON = JSON.parse(options) as PublicKeyCredentialCreationOptionsJSON
    void Passkey.startRegistration({
      options,
      challenge: optionsJSON.challenge,
      rpId: optionsJSON.rp.id,
      userName: optionsJSON.user.name,
      userId: optionsJSON.user.id,
    })
    Passkey.addListener('CAP_PLUGIN_ANDROID_CREATE_PASSKEY_SUCCESS', (data: unknown) => {
      const res = data as { data: string }
      resolve(res.data)
    })
    Passkey.addListener('CAP_PLUGIN_IOS_CREATE_PASSKEY_SUCCESS', (data: unknown) => {
      const res = data as {
        clientDataJSON: string
        attestationObject: string
        rawId: string
        id: string
      }
      const response = {
        id: res.id,
        rawId: res.rawId,
        response: {
          attestationObject: res.attestationObject,
          clientDataJSON: res.clientDataJSON,
          transports: ['internal', 'hybrid'],
          publicKeyAlgorithm: -7,
        },
        type: 'public-key',
        clientExtensionResults: {
          credProps: {
            rk: true,
          },
        },
        authenticatorAttachment: 'platform',
      }
      resolve(JSON.stringify(response))
    })
    Passkey.addListener('CAP_PLUGIN_NATIVE_PASSKEY_ERROR_OR_CANCELLED', (data: unknown) => {
      reject(new Error(data as string))
    })
  })

const startRegistration = async (options: string): Promise<string> => {
  if (Capacitor.getPlatform() === 'web') {
    // Validate user gesture before WebAuthn operation
    const gestureValidation = gestureDetector.validateGestureRequirements(WebAuthnOperation.REGISTRATION)
    if (!gestureValidation.isValid) {
      log.warn('User gesture validation failed for registration', {
        reason: gestureValidation.reason,
        browserType: gestureDetector.getBrowserType(),
      })
      const error = new PasskeyError(PasskeyErrorTypes.USER_GESTURE_REQUIRED)
      error.message = gestureDetector.getUserGuidanceMessage()
      throw error
    }

    const res = await startRegistrationWeb({
      optionsJSON: JSON.parse(options) as PublicKeyCredentialCreationOptionsJSON,
    })
    return JSON.stringify(res)
  } else if (Capacitor.isNativePlatform()) {
    return await startRegistrationNative(options)
  }
  throw new Error('Unsupported platform')
}

export const registerPasskey = async (): Promise<RegisterWebAuthnOutput> => {
  try {
    const isMobileWithPlatformAuthenticator = await getIsMobileWithPlatformAuthenticator()
    sendEvent('device_requestRegistrationCredentialOptions_submit', {
      eventSource: 'egWealth',
      category: 'passkey-add',
      subCategory: 'registration-options',
    })
    const keyRes = await authsvsStartWebAuthnRegistration({
      isMobileWithPlatformAuthenticator,
    })
    sendEvent('device_requestRegistrationCredentialOptions_success', {
      eventSource: 'egWealth',
      category: 'passkey-add',
      subCategory: 'registration-options',
    })
    if (keyRes.__typename === 'StartWebAuthnRegistrationError') {
      sendEvent('device_requestRegistrationCredentialOptions_fail', {
        eventSource: 'egWealth',
        category: 'passkey-add',
        subCategory: 'registration-options',
      })
      throw new PasskeyError(keyRes.errorType as unknown as PasskeyErrorTypes)
    }
    sendEvent('device_RegistrationCredential_start', {
      eventSource: 'egWealth',
      category: 'passkey-add',
      subCategory: 'registration-options',
    })
    const asseRes = await startRegistration(keyRes.publicKeyCredentialCreationOptions)
    sendEvent('device_RegistrationCredential_success', {
      eventSource: 'egWealth',
      category: 'passkey-add',
      subCategory: 'registration-options',
    })
    sendEvent('device_RegistrationCredential_submit', {
      eventSource: 'egWealth',
      category: 'passkey-add',
      subCategory: 'registration-options',
    })
    const verificationRes = await authsvsRegisterWebAuthn({ publicKeyCredentials: asseRes })
    if (verificationRes.__typename === 'RegisterWebAuthnError') {
      sendEvent('eg_passkeyAdd_fail', {
        eventSource: 'egWealth',
        category: 'passkey-add',
        subCategory: 'result',
      })
      const response = verificationRes
      throw new PasskeyError(response.errorType as unknown as PasskeyErrorTypes)
    }
    sendEvent('device_RegistrationCredential_submit', {
      eventSource: 'egWealth',
      category: 'passkey-add',
      subCategory: 'result',
    })
    return verificationRes
  } catch (err) {
    sendEvent('eg_passkeyAdd_error', {
      eventSource: 'egWealth',
      category: 'passkey-add',
      subCategory: 'result',
      error: JSON.stringify(err),
    })
    // Check if errorType is undefined and set it to UNKNOWN - handles JSON.parse errors and startAuthentication errors
    const error = err as PasskeyError
    if (!error.errorType) {
      error.errorType = PasskeyErrorTypes.UNKNOWN
    }
    throw error
  }
}

export const createPasskeyCredentials = async (publicKeyCredentialCreationOptions: string): Promise<string> => {
  try {
    const response = await startRegistration(publicKeyCredentialCreationOptions)
    return response
  } catch (err) {
    // Check if errorType is undefined and set it to UNKNOWN - handles JSON.parse errors
    const error = err as PasskeyError
    if (!error.errorType) {
      error.errorType = PasskeyErrorTypes.UNKNOWN
    }
    throw error
  }
}

let instance: PasskeyService | null = null

class PasskeyService {
  private isInitialized = false
  private hasPointerCoarse: boolean | undefined
  private hasMobileUserAgent: boolean | undefined
  private hasTouchScreen: boolean | undefined
  private hasOrientation: boolean | undefined
  private canUsePasskey: boolean | undefined
  private hasPlatformAuthenticator: boolean | undefined
  private isConditionalMediationAvailable: boolean | undefined
  private isMobile: boolean | undefined

  constructor() {
    if (!instance) {
      instance = this
    }
  }

  async init(): Promise<void> {
    if (!this.isInitialized) {
      if (typeof window !== 'undefined') {
        this.hasPointerCoarse = isUserUsingMobileDevice().deviceChecks.hasPointerCoarse as boolean
        this.hasMobileUserAgent = isUserUsingMobileDevice().deviceChecks.hasMobileUserAgent as boolean
        this.hasTouchScreen = isUserUsingMobileDevice().deviceChecks.hasTouchScreen as boolean
        this.hasOrientation = isUserUsingMobileDevice().deviceChecks.hasOrientation as boolean

        this.canUsePasskey = browserSupportsWebAuthn()
        this.hasPlatformAuthenticator = await platformAuthenticatorIsAvailable()
        this.isConditionalMediationAvailable = await browserSupportsWebAuthnAutofill()
        this.isMobile = isUserUsingMobileDevice().isDeviceMobile

        notifications.dispatch(NOTIFICATIONS.PASSKEY_SERVICE.INIT, {
          hasPointerCoarse: this.hasPointerCoarse,
          hasMobileUserAgent: this.hasMobileUserAgent,
          hasTouchScreen: this.hasTouchScreen,
          hasOrientation: this.hasOrientation,
          canUsePasskey: this.canUsePasskey,
          hasPlatformAuthenticator: this.hasPlatformAuthenticator,
          isConditionalMediationAvailable: this.isConditionalMediationAvailable,
          isMobile: this.isMobile,
        })
      }
      this.isInitialized = true
      log.info('Passkey Service service initialized')
    } else {
      log.warn('Passkey Service service is already initialized. Skipping initialization...')
    }
  }

  getHasPointerCoarse(): boolean | undefined {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (this.hasPointerCoarse === undefined) {
      log.error('hasPointerCoarse is undefined')
      throw new Error('hasPointerCoarse is undefined')
    }
    return this.hasPointerCoarse
  }

  getHasMobileUserAgent(): boolean | undefined {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (this.hasMobileUserAgent === undefined) {
      log.error('hasMobileUserAgent is undefined')
      throw new Error('hasMobileUserAgent is undefined')
    }
    return this.hasMobileUserAgent
  }

  getHasTouchScreen(): boolean | undefined {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (this.hasTouchScreen === undefined) {
      log.error('hasTouchScreen is undefined')
      throw new Error('hasTouchScreen is undefined')
    }
    return this.hasTouchScreen
  }

  getHasOrientation(): boolean | undefined {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (this.hasOrientation === undefined) {
      log.error('hasOrientation is undefined')
      throw new Error('hasOrientation is undefined')
    }
    return this.hasOrientation
  }

  getCanUsePasskey(): boolean | undefined {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (this.canUsePasskey === undefined) {
      log.error('canUsePasskey is undefined')
      throw new Error('canUsePasskey is undefined')
    }
    return this.canUsePasskey
  }

  getHasPlatformAuthenticator(): boolean | undefined {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (this.hasPlatformAuthenticator === undefined) {
      log.error('hasPlatformAuthenticator is undefined')
      throw new Error('hasPlatformAuthenticator is undefined')
    }
    return this.hasPlatformAuthenticator
  }

  getIsConditionalMediationAvailable(): boolean | undefined {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (this.isConditionalMediationAvailable === undefined) {
      log.error('isConditionalMediationAvailable is undefined')
      throw new Error('isConditionalMediationAvailable is undefined')
    }
    return this.isConditionalMediationAvailable
  }

  getIsMobile(): boolean | undefined {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (this.isMobile === undefined) {
      log.error('isMobile is undefined')
      throw new Error('isMobile is undefined')
    }
    return this.isMobile
  }

  /**
   * Signals that a credential does not exist on the server
   * @param credentialId The base64url encoded credential ID
   */
  async signalUnknownCredential(credentialId: string): Promise<void> {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }

    if (typeof PublicKeyCredential !== 'undefined' && 'signalUnknownCredential' in PublicKeyCredential) {
      const rpId = window.location.hostname
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        await (PublicKeyCredential as any).signalUnknownCredential({
          rpId,
          credentialId,
        })
        log.info('Successfully signaled unknown credential', { rpId, credentialId })
      } catch (error) {
        log.error('Failed to signal unknown credential', { error, rpId, credentialId })
        throw error
      }
    } else {
      log.warn('signalUnknownCredential is not supported in this browser')
    }
  }

  /**
   * Signals a list of all accepted credentials for a user
   * @param userId The base64url encoded user ID
   * @param allAcceptedCredentialIds Array of base64url encoded credential IDs
   */
  async signalAllAcceptedCredentials(userId: string, allAcceptedCredentialIds: string[]): Promise<void> {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }

    if (typeof PublicKeyCredential !== 'undefined' && 'signalAllAcceptedCredentials' in PublicKeyCredential) {
      const rpId = window.location.hostname
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        await (PublicKeyCredential as any).signalAllAcceptedCredentials({
          rpId,
          userId,
          allAcceptedCredentialIds,
        })
        log.info('Successfully signaled all accepted credentials', { rpId, userId })
      } catch (error) {
        log.error('Failed to signal all accepted credentials', { error, rpId, userId })
        throw error
      }
    } else {
      log.warn('signalAllAcceptedCredentials is not supported in this browser')
    }
  }

  /**
   * Signals updated user details
   * @param userId The base64url encoded user ID
   * @param name The username
   * @param displayName The display name
   */
  async signalCurrentUserDetails(userId: string, name: string, displayName: string): Promise<void> {
    if (!this.isInitialized) {
      log.error('Service is not initialized')
      throw new Error('Service is not initialized')
    }
    if (typeof PublicKeyCredential !== 'undefined' && 'signalCurrentUserDetails' in PublicKeyCredential) {
      const rpId = window.location.hostname
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
        await (PublicKeyCredential as any).signalCurrentUserDetails({
          rpId,
          userId: btoa(userId).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, ''),
          name,
          displayName,
        })
        log.info('Successfully signaled current user details', { rpId, userId, name, displayName })
      } catch (error) {
        log.error('Failed to signal current user details', { error, rpId, userId })
        throw error
      }
    } else {
      log.warn('signalCurrentUserDetails is not supported in this browser')
    }
  }
}

export const passkeyService = new PasskeyService()
